import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Loader2, ExternalLink, Newspaper, Building2, FileText, Users } from 'lucide-react';
import { pathId } from '../lib/pathId';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  news:      { icon: Newspaper, route: 'news',      color: 'text-blue-600',    label: 'News' },
  tenders:   { icon: FileText,  route: 'tenders',   color: 'text-orange-500',  label: 'Tender' },
  projects:  { icon: Building2, route: 'projects',  color: 'text-primary',     label: 'Project' },
  companies: { icon: Users,     route: 'companies', color: 'text-emerald-600', label: 'Company' },
};

const getDisplayName = (type, item) => {
  if (!item) return 'Unknown';
  if (type === 'news') return item.title_en || (typeof item.title === 'string' ? item.title : '') || 'Untitled';
  if (type === 'tenders') {
    const t = item.title;
    return (typeof t === 'object' ? t?.en || t?.ar : t) || item.canonical_name || 'Untitled';
  }
  return item.canonical_name || item.name || 'Untitled';
};

export default function LinkedItemsPopover({ ids, type, token, children }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  if (!ids?.length) return <>{children}</>;

  const config = TYPE_CONFIG[type];
  const Icon = config.icon;

  const handleOpenChange = async (isOpen) => {
    setOpen(isOpen);
    if (isOpen && !fetched) {
      setLoading(true);
      try {
        const results = await Promise.all(
          ids.map(id =>
            fetch(`${API_URL}/api/intelligence/${type}/${pathId(id)}`, {
              headers: token ? { Authorization: `Bearer ${token}` } : {}
            }).then(r => r.ok ? r.json() : null)
          )
        );
        setItems(results.filter(Boolean));
      } catch {
        // silently fail
      } finally {
        setFetched(true);
        setLoading(false);
      }
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <div onClick={(e) => e.stopPropagation()}>
          {children}
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="w-72 p-2"
        align="start"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-2 mb-2">
          {(fetched ? items.length : ids.length)} Linked {config.label}{(fetched ? items.length : ids.length) !== 1 ? 's' : ''}
        </p>
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-xs text-muted-foreground px-2 py-3 text-center">
            No {config.label.toLowerCase()}s found — linked items may have been removed.
          </p>
        ) : (
          <div className="space-y-0.5 max-h-60 overflow-y-auto">
            {items.map(item => (
              <button
                key={item._id}
                className="w-full flex items-center gap-2 px-2 py-2 rounded-md hover:bg-muted/60 text-left transition-colors group"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  navigate(`/dashboard/${config.route}?filter_ids=${encodeURIComponent(item._id)}`);
                }}
              >
                <Icon className={`w-3.5 h-3.5 shrink-0 ${config.color}`} />
                <span className="text-sm truncate flex-1">{getDisplayName(type, item)}</span>
                <ExternalLink className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 shrink-0" />
              </button>
            ))}
          </div>
        )}
        {!loading && items.length > 1 && (
          <div className="border-t border-border mt-2 pt-2">
            <button
              className="w-full text-xs text-primary hover:underline text-left px-2 py-1 rounded hover:bg-muted/40 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                navigate(`/dashboard/${config.route}?filter_ids=${ids.map(encodeURIComponent).join(',')}`);
              }}
            >
              View all {ids.length} linked {config.label}s →
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
